'use client'

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Box, ContentText, fontWeights } from '@fortum/elemental-ui'

import type { CostDetails } from '@/open-web/services/calculators/calculateContractTemplateCost'
import { getPriceBaseOnCustomerType } from '@/open-web/utils/getPriceBaseOnCustomerType'
import { usePriceToString } from '@/shared/hooks/usePriceToStringFormatter'
import { useTheme } from '@/shared/hooks/useTheme'
import { logError } from '@/shared/utils/error'
import { UiError } from '@/shared/utils/errorClasses'

import type { PriceProps } from '../types'
import { EMPTY_ADDONS, defineTheme, discountPercentToString, validateCustomerType } from '../utils'
import { calculateTotalPrice } from './calculateTotalPrice'

export const NorwegianPrice: React.FC<PriceProps> = ({
  contractTemplate,
  theme = 'light',
  priceSize = 'xl',
  addonsTariffNos = EMPTY_ADDONS,
  campaignConfiguration,
  variant = 'mainProduct',
}) => {
  const { error: contractCustomerTypeError } = validateCustomerType(contractTemplate)
  const { colors } = useTheme()
  const tPurchaseFlow = useTranslations('purchaseFlow')
  const tAriaLabels = useTranslations('ariaLabels')
  const { formatPriceWithUnit, formatPrice } = usePriceToString()

  const calculatedTotalPrice = useMemo(
    () =>
      calculateTotalPrice({
        contractTemplate,
        selectedAddonsTariffNos: addonsTariffNos,
      }),
    [contractTemplate, addonsTariffNos],
  )

  const {
    mainProduct,
    total: calculatedTotal,
    totalPriceWithDiscount: calculatedTotalWithDiscount,
    hasSelectedAddonWithDiscount,
  } = calculatedTotalPrice || {}

  const colorConfiguration = defineTheme(colors, theme)

  const getPriceString = (cost: CostDetails) => {
    const price = getPriceBaseOnCustomerType(cost, contractTemplate.customerType)
    return formatPriceWithUnit(price, cost.priceUnit)
  }

  const total = variant === 'mainProduct' ? mainProduct?.total : calculatedTotal
  const totalPriceWithDiscount =
    variant === 'mainProduct' ? mainProduct?.totalPriceWithDiscount : calculatedTotalWithDiscount
  const discount = mainProduct?.discount
  const hasDiscount = Boolean(discount) || hasSelectedAddonWithDiscount

  const discountedPrice = getPriceBaseOnCustomerType(
    discount?.type === 'energy'
      ? totalPriceWithDiscount?.energy
      : totalPriceWithDiscount?.monthlyFee,
    contractTemplate.customerType,
  )

  const totalPrice = getPriceBaseOnCustomerType(
    discount?.type === 'energy' ? total?.energy : total?.monthlyFee,
    contractTemplate.customerType,
  )

  if (contractCustomerTypeError) {
    logError(contractCustomerTypeError)
    return null
  }

  if (!totalPriceWithDiscount || !total) {
    logError(
      new UiError(
        `Cannot show price for campaign: ${campaignConfiguration.name} for contract template: ${contractTemplate.templateName}. Calculated total price is missing.`,
        `NorwegianPrice`,
      ),
    )
    return null
  }

  return (
    <Box>
      <ContentText
        size={priceSize}
        fontWeight={fontWeights.medium}
        color={colorConfiguration.price}
        aria-label={hasDiscount ? tAriaLabels('discountPrice') : tAriaLabels('price')}
        data-testid="contract-price"
      >
        {`${getPriceString(totalPriceWithDiscount.monthlyFee)} + ${getPriceString(totalPriceWithDiscount.energy)}`}
      </ContentText>

      {hasDiscount && (
        <>
          <CrossedOutText
            aria-label={tAriaLabels('originalPrice')}
            size="s"
            data-testid="crossed-out-price"
            color={colorConfiguration.crossedPrice}
          >
            {`${getPriceString(total.monthlyFee)} + ${getPriceString(total.energy)}`}
          </CrossedOutText>

          {discount && (
            <ContentText size="xs" color={colorConfiguration.duration} textWrap="word">
              {tPurchaseFlow('discount', {
                discountDuration: discount?.duration?.amount ?? 'none',
                discountDurationType: discount?.duration?.type,
                discountType: discount.type ?? 'unknown',
                discountPercent: discountPercentToString(totalPrice, discountedPrice, formatPrice),
              })}
            </ContentText>
          )}
        </>
      )}
    </Box>
  )
}

const CrossedOutText = styled(ContentText)`
  text-decoration: line-through;
`
